export type RearrangeMove = { from: number; to: number }
export type RearrangeState = { pendingFrom: number | null; moves: RearrangeMove[] }

/** Stato iniziale di una sessione di spostamento: nessuna mossa, nessuna selezione a metà. */
export function startMove(): RearrangeState {
  return { pendingFrom: null, moves: [] }
}

/**
 * Applica il clic su un bicchiere durante la selezione: il primo (attivo, non preso) apre una mossa, il
 * secondo (posizione vuota, presa) la chiude. `taken` è lo stato REALE dei bicchieri, non ancora toccato
 * da nessuna mossa di questa sessione (le mosse si applicano solo alla conferma): un bicchiere già usato
 * come `from` o `to` in una mossa precedente della sessione non può essere riselezionato, anche se `taken`
 * da solo non lo vieterebbe.
 */
export function applySelection(state: RearrangeState, cupIndex: number, taken: ReadonlySet<number>): RearrangeState {
  if (state.pendingFrom === null) {
    if (taken.has(cupIndex) || state.moves.some((move) => move.from === cupIndex)) return state
    return { ...state, pendingFrom: cupIndex }
  }
  if (!taken.has(cupIndex) || state.moves.some((move) => move.to === cupIndex)) return state
  return { pendingFrom: null, moves: [...state.moves, { from: state.pendingFrom, to: cupIndex }] }
}

/** Mosse completate finora, nel formato richiesto da `POST .../rearrangements`. */
export function toMoves(state: RearrangeState): RearrangeMove[] {
  return state.moves
}

/**
 * Anteprima dello spostamento non ancora confermato, per il disegno del triangolo: `taken` con le mosse applicate,
 * le posizioni che possono ancora ricevere un bicchiere (vuote davvero e non già usate come `to`) e i bicchieri
 * già spostati. Non cambia la validazione, che resta su `applySelection` con lo stato reale.
 */
export function previewRearrangement(
  taken: ReadonlySet<number>,
  state: RearrangeState,
): { taken: Set<number>; targets: Set<number>; moved: Set<number> } {
  const preview = new Set(taken)
  for (const move of state.moves) {
    preview.delete(move.to)
    preview.add(move.from)
  }
  const usedTargets = new Set(state.moves.map((move) => move.to))
  const targets = new Set([...taken].filter((cup) => !usedTargets.has(cup)))
  return { taken: preview, targets, moved: usedTargets }
}
